/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Telegram OAuth login widget — renders Telegram's official login button via script injection
 *   PROPS:   botId (client_id from BotFather), onAuth callback with id_token
 *   STATE:   scriptLoaded (tracks whether telegram-login.js has been injected)
 *   DEPENDS: M-CN (cn utility), API-CLIENT
 *   EMITS:   onAuth(id_token) on successful OIDC authentication
 *   ACCESSIBILITY: Button rendered by widget with role=button and tabindex=0
 *   LINKS:   M-TGL
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-06-13] Created — Telegram OAuth widget for login/register pages
 *   [2026-06-13] Added TelegramAuthButton — client wrapper for use from Server Components
 *   [2026-06-13] Fixed widget not rendering — adapted to new OIDC-based widget API
 *   [2026-06-13] Updated onAuth to pass id_token (JWT) for backend verification
 */

"use client"

import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { cn } from "@/lib/cn"
import { useAuth } from "@/hooks/use-auth"

export interface OidcUser {
  iss: string
  aud: string
  sub: string
  iat: number
  exp: number
  id: number
  name: string
  preferred_username: string
  picture: string
  phone_number?: string
}

interface TelegramLoginProps {
  botId: string
  onAuth?: (idToken: string, user: OidcUser) => void
  className?: string
}

// --- Global script loader (load widget script only once) ---

const SCRIPT_URL = "https://telegram.org/js/telegram-login.js"

let scriptLoaded = false
let scriptLoading = false
const pendingCallbacks: Array<() => void> = []

function loadWidgetScript(onLoaded: () => void) {
  if (scriptLoaded) {
    onLoaded()
    return
  }

  pendingCallbacks.push(onLoaded)

  if (scriptLoading) return
  scriptLoading = true

  const script = document.createElement("script")
  script.async = true
  script.src = SCRIPT_URL
  script.onload = () => {
    scriptLoaded = true
    pendingCallbacks.splice(0).forEach((cb) => cb())
  }
  document.head.appendChild(script)
}

// --- TelegramLoginComponent ---

export function TelegramLogin({ botId, onAuth, className }: TelegramLoginProps) {
  const [ready, setReady] = useState(scriptLoaded)
  const onAuthRef = useRef(onAuth)
  onAuthRef.current = onAuth

  useEffect(() => {
    loadWidgetScript(() => setReady(true))
  }, [])

  useEffect(() => {
    if (!ready) return
    const win = window as unknown as Record<string, unknown>
    const tg = win.Telegram as Record<string, unknown> | undefined
    const login = tg?.Login as Record<string, unknown> | undefined
    const init = login?.init as ((opts: Record<string, unknown>, cb: (data: unknown) => void) => void) | undefined

    if (init) {
      init(
        { client_id: botId, request_access: "write" },
        (data: unknown) => {
          const result = data as { id_token?: string; user?: OidcUser }
          if (result.id_token && result.user) {
            onAuthRef.current?.(result.id_token, result.user)
          }
        }
      )
    }
  }, [ready, botId])

  return (
    <div className={cn("flex justify-center", className)}>
      <button
        type="button"
        className="tg-auth-button"
        onClick={() => {
          const win = window as unknown as Record<string, unknown>
          const tg = win.Telegram as Record<string, unknown> | undefined
          const login = tg?.Login as Record<string, unknown> | undefined
          const open = login?.open as ((cb?: unknown) => void) | undefined
          open?.()
        }}
      >
        Log in with Telegram
      </button>
    </div>
  )
}

// --- TelegramAuthButton (client wrapper for use from Server Components) ---

export function TelegramAuthButton({ botId, className }: { botId: string; className?: string }) {
  const router = useRouter()
  const { login } = useAuth()

  return (
    <TelegramLogin
      botId={botId}
      className={className}
      onAuth={async (idToken: string) => {
        try {
          await login(idToken)
          const locale = window.location.pathname.split("/")[1] || "en"
          router.push(`/${locale}/dashboard`)
        } catch {
          // auth failure — user stays on login page
        }
      }}
    />
  )
}
